import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr, API_BASE } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth';
import { MaskedAmount } from '@/components/common/MaskedAmount';
import { StatusChip } from '@/components/common/StatusChip';
import { STAGE_LABEL } from '@/lib/roles';
import { RagDonut } from '@/components/common/RagDonut';
import { HealthTrendChart } from '@/components/common/HealthTrendChart';
import { SlipReasonsPanel } from '@/components/common/SlipReasonsPanel';
import { RevenueOutstandingBar, PerProjectPaymentBars } from '@/components/reports/FinancialCharts';
import { WorkloadHeatBar, WorkloadHeatLegend } from '@/components/reports/WorkloadHeatBar';
import { MilestoneSlipTrend } from '@/components/reports/MilestoneSlipTrend';
import {
  Table as TableIcon, FileSpreadsheet, FileText, TrendingUp, Wallet, Users,
  ListChecks, Filter, RefreshCcw, Play, Save, Trash2, FileDown, AlertTriangle, CheckCircle2,
} from 'lucide-react';

const RAG_LABEL = {
  on_track: 'On track', at_risk: 'At risk', delayed: 'Delayed',
  on_hold: 'On hold', closed: 'Closed',
};

const TABS = [
  { key: 'project_health',    label: 'Project Health',    Icon: TrendingUp,  api: 'project-health' },
  { key: 'financial_summary', label: 'Financial Summary', Icon: Wallet,      api: 'financial-summary' },
  { key: 'team_workload',     label: 'Team Workload',     Icon: Users,       api: 'team-workload' },
  { key: 'milestone_tracker', label: 'Milestone Tracker', Icon: ListChecks,  api: 'milestone-tracker' },
  { key: 'export_builder',    label: 'Export Builder',    Icon: TableIcon,   api: null },
];

const inr = (n) => n == null ? '—' : '₹\u00a0' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n);

export default function ReportsPage() {
  const { user } = useAuth();
  const [available, setAvailable] = useState({});
  const [tab, setTab] = useState(null);
  const [filters, setFilters] = useState({ from: '', to: '', project_id: '', member_id: '', client: '', scope: 'all' });
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);

  const [availableLoaded, setAvailableLoaded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    (async () => {
      try {
        const [av, u, p] = await Promise.all([
          api.get('/reports/insights/available'),
          api.get('/users'),
          api.get('/master-projects/for-mapping').catch(() => ({ data: [] })),
        ]);
        setAvailable(av.data.available || {});
        setUsers(u.data || []);
        setProjects(p.data || []);
      } catch (e) { toast.error(parseErr(e)); }
      finally { setAvailableLoaded(true); }
    })();
  }, []);

  useEffect(() => {
    if (!availableLoaded || tab) return;
    const urlTab = searchParams.get('tab');
    if (urlTab && (urlTab === 'export_builder' || available[urlTab])) { setTab(urlTab); return; }
    const first = TABS.find(t => available[t.key]) || TABS.find(t => t.key === 'export_builder');
    if (first) setTab(first.key);
  }, [available, availableLoaded, tab, searchParams]);

  const switchTab = (k) => { setTab(k); setSearchParams({ tab: k }, { replace: true }); };

  const visibleTabs = TABS.filter(t => t.key === 'export_builder' || available[t.key]);

  return (
    <AppShell breadcrumbs={[{ label: 'Reports' }]}>
      <div className="p-6 max-w-[1400px] mx-auto space-y-4 fade-up" data-testid="reports-page">
        <div>
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
            <TableIcon size={20} />Reports
          </h1>
          <p className="text-[13px] text-[hsl(var(--foreground-muted))] mt-1">
            Insight views for the leadership rhythm. Filter, then download as Excel or A4 PDF.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="seg" data-testid="reports-tabs">
            {visibleTabs.map(t => (
              <button key={t.key} onClick={() => switchTab(t.key)}
                      className={tab === t.key ? 'active' : ''}
                      data-testid={`reports-tab-${t.key}`}>
                <t.Icon size={12} className="inline mr-1" />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {tab && tab !== 'export_builder' && (
          <FilterBar filters={filters} setFilters={setFilters}
                     users={users} projects={projects}
                     showClient={tab === 'financial_summary'}
                     showMember={tab !== 'financial_summary'}
                     showScope={tab === 'milestone_tracker'} />
        )}

        {tab === 'project_health'    && <ProjectHealthTab filters={filters} />}
        {tab === 'financial_summary' && <FinancialSummaryTab filters={filters} />}
        {tab === 'team_workload'     && <TeamWorkloadTab filters={filters} />}
        {tab === 'milestone_tracker' && <MilestoneTrackerTab filters={filters} />}
        {tab === 'export_builder'    && <ExportBuilderTab currentUser={user} />}
      </div>
    </AppShell>
  );
}

// ------------------------------- FILTER BAR --------------------------------
const FilterBar = ({ filters, setFilters, users, projects, showClient, showMember, showScope }) => {
  const set = (k, v) => setFilters(p => ({ ...p, [k]: v }));
  return (
    <div className="card p-3 flex flex-wrap items-end gap-2" data-testid="reports-filters">
      <div>
        <div className="text-[10.5px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">From</div>
        <Input type="date" value={filters.from} onChange={e => set('from', e.target.value)} className="h-8 w-[140px] text-[12.5px]" data-testid="reports-filter-from" />
      </div>
      <div>
        <div className="text-[10.5px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">To</div>
        <Input type="date" value={filters.to} onChange={e => set('to', e.target.value)} className="h-8 w-[140px] text-[12.5px]" data-testid="reports-filter-to" />
      </div>
      <div>
        <div className="text-[10.5px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">Project</div>
        <select className="h-8 border rounded-md px-2 text-[12.5px] min-w-[180px]"
                style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-0))' }}
                value={filters.project_id} onChange={e => set('project_id', e.target.value)}
                data-testid="reports-filter-project">
          <option value="">All projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
        </select>
      </div>
      {showMember && (
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">Team member</div>
          <select className="h-8 border rounded-md px-2 text-[12.5px] min-w-[180px]"
                  style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-0))' }}
                  value={filters.member_id} onChange={e => set('member_id', e.target.value)}
                  data-testid="reports-filter-member">
            <option value="">Everyone</option>
            {users.filter(u => u.status === 'active').map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      )}
      {showClient && (
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">Client contains</div>
          <Input value={filters.client} onChange={e => set('client', e.target.value)}
                 placeholder="e.g. Acme" className="h-8 w-[180px] text-[12.5px]"
                 data-testid="reports-filter-client" />
        </div>
      )}
      {showScope && (
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">Show</div>
          <select className="h-8 border rounded-md px-2 text-[12.5px]"
                  style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-0))' }}
                  value={filters.scope} onChange={e => set('scope', e.target.value)}
                  data-testid="reports-filter-scope">
            <option value="all">All milestones</option>
            <option value="upcoming">Upcoming only</option>
            <option value="overdue">Overdue only</option>
          </select>
        </div>
      )}
      <Button size="sm" variant="ghost" onClick={() => setFilters({ from: '', to: '', project_id: '', member_id: '', client: '', scope: 'all' })}
              data-testid="reports-filter-clear">
        Clear filters
      </Button>
    </div>
  );
};

// ------------------------------ EXPORT BUTTONS -----------------------------
const ExportButtons = ({ reportKey, filters }) => {
  const download = async (fmt) => {
    try {
      const token = localStorage.getItem('32d.os.token');
      const qs = new URLSearchParams();
      if (filters.from) qs.set('from', filters.from);
      if (filters.to) qs.set('to', filters.to);
      if (filters.project_id) qs.set('project_id', filters.project_id);
      if (filters.member_id) qs.set('member_id', filters.member_id);
      if (filters.client) qs.set('client', filters.client);
      if (filters.scope) qs.set('scope', filters.scope);
      qs.set('format', fmt);
      const res = await fetch(`${API_BASE}/reports/insights/${reportKey}/export?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const t = await res.text(); throw new Error(t); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = fmt === 'xlsx' ? 'xlsx' : 'pdf';
      a.download = `32DEC-${reportKey}-${new Date().toISOString().slice(0,10)}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ext.toUpperCase()}`);
    } catch (e) { toast.error(String(e).slice(0, 200)); }
  };
  return (
    <div className="flex gap-1">
      <Button size="sm" variant="outline" onClick={() => download('xlsx')} data-testid={`export-xlsx-${reportKey}`}>
        <FileSpreadsheet size={13} className="mr-1" />Excel
      </Button>
      <Button size="sm" variant="outline" onClick={() => download('pdf')} data-testid={`export-pdf-${reportKey}`}>
        <FileText size={13} className="mr-1" />PDF (A4)
      </Button>
    </div>
  );
};

// ----------------------------- PROJECT HEALTH ------------------------------
const useInsight = (path, filters) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const params = useMemo(() => {
    const q = {};
    if (filters.from) q.from = filters.from;
    if (filters.to) q.to = filters.to;
    if (filters.project_id) q.project_id = filters.project_id;
    if (filters.member_id) q.member_id = filters.member_id;
    if (filters.client) q.client = filters.client;
    if (filters.scope) q.scope = filters.scope;
    return q;
  }, [filters]);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/reports/insights/${path}`, { params }).then(r => { if (alive) setData(r.data); })
      .catch(e => toast.error(parseErr(e))).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [path, params]);
  return [data, loading];
};

const KpiTile = ({ label, value, tone = 'neutral', testId }) => {
  const toneCls = {
    neutral: 'text-[hsl(var(--foreground))]',
    danger: 'text-[hsl(var(--danger))]',
    warning: 'text-[hsl(var(--warning))]',
    success: 'text-[hsl(var(--success))]',
    info: 'text-[hsl(var(--info))]',
  }[tone];
  return (
    <div className="kpi-tile" data-testid={testId}>
      <div className="kpi-tile__label">{label}</div>
      <div className={`kpi-tile__value ${toneCls}`}>{value}</div>
    </div>
  );
};

const ProjectHealthTab = ({ filters }) => {
  const [data, loading] = useInsight('project-health', filters);
  if (loading || !data) return <LoadingCard />;
  return (
    <div className="space-y-3" data-testid="reports-project-health">
      <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-3">
        <div className="card p-4 elev-1" data-testid="ph-donut-card">
          <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold mb-2">RAG breakdown</div>
          <RagDonut counts={data.counts} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 self-start">
          <KpiTile label="On track" value={data.counts.on_track || 0} tone="success" testId="ph-kpi-on-track" />
          <KpiTile label="At risk"  value={data.counts.at_risk || 0}  tone="warning" testId="ph-kpi-at-risk" />
          <KpiTile label="Delayed"  value={data.counts.delayed || 0}  tone="danger"  testId="ph-kpi-delayed" />
          <KpiTile label="On hold"  value={data.counts.on_hold || 0}  tone="neutral" testId="ph-kpi-on-hold" />
          <KpiTile label="Closed"   value={data.counts.closed || 0}   tone="neutral" testId="ph-kpi-closed" />
        </div>
      </div>

      <HealthTrendChart weeks={8} />
      <SlipReasonsPanel weeks={8} />

      <div className="flex justify-between items-center">
        <div className="text-[12px] text-[hsl(var(--foreground-muted))]">
          {data.total} project{data.total === 1 ? '' : 's'} · As of {data.as_of}
        </div>
        <ExportButtons reportKey="project_health" filters={filters} />
      </div>

      <div className="card overflow-x-auto elev-1">
        <table className="tbl">
          <thead>
            <tr>
              <th>RAG</th>
              <th>Project</th>
              <th>Client</th>
              <th>Stage</th>
              <th>Milestones</th>
              <th>Tenure end</th>
              <th>Leads</th>
              <th>Team</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.id} data-testid={`ph-row-${r.id}`}>
                <td>
                  <div className="flex items-center gap-2">
                    <span className={`rag-dot rag-${r.rag}`} title={r.rag_reason} />
                    <span className="text-[12.5px] font-medium">{RAG_LABEL[r.rag] || r.rag}</span>
                  </div>
                  <div className="text-[10.5px] text-[hsl(var(--foreground-subtle))] mt-0.5 max-w-[240px]">
                    {r.rag_reason}
                  </div>
                </td>
                <td className="font-medium">{r.project_name}</td>
                <td>{r.client}</td>
                <td><span className="chip chip-info">{STAGE_LABEL[r.project_stage] || r.project_stage}</span></td>
                <td className="tabular-nums text-[12.5px]">
                  <span className={r.overdue_milestones ? 'text-[hsl(var(--danger))] font-medium' : ''}>
                    {r.milestones_done}/{r.milestones_total}
                  </span>
                  {r.overdue_milestones > 0 && (
                    <span className="ml-1 text-[hsl(var(--danger))] text-[11px]">
                      · {r.overdue_milestones} overdue
                    </span>
                  )}
                </td>
                <td className="tabular-nums text-[12.5px]">
                  {r.tenure_end_date || '—'}
                  {r.days_to_tenure_end != null && (
                    <span className={`ml-1 text-[11px] ${r.days_to_tenure_end < 0 ? 'text-[hsl(var(--danger))]' : r.days_to_tenure_end <= 14 ? 'text-[hsl(var(--warning))]' : 'text-[hsl(var(--foreground-subtle))]'}`}>
                      ({r.days_to_tenure_end < 0 ? `${-r.days_to_tenure_end}d past` : `${r.days_to_tenure_end}d left`})
                    </span>
                  )}
                </td>
                <td className="text-[12px]">
                  {r.creative_lead_name && <div>Creative: {r.creative_lead_name}</div>}
                  {r.technical_lead_name && <div>Technical: {r.technical_lead_name}</div>}
                  {!r.creative_lead_name && !r.technical_lead_name && <span className="italic text-[hsl(var(--foreground-subtle))]">Unassigned</span>}
                </td>
                <td className="tabular-nums">{r.team_size}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------- FINANCIAL SUMMARY ----------------------------
const FinancialSummaryTab = ({ filters }) => {
  const [data, loading] = useInsight('financial-summary', filters);
  if (loading || !data) return <LoadingCard />;
  const totals = data.totals;
  return (
    <div className="space-y-3" data-testid="reports-financial-summary">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KpiTile label="Total value" value={inr(totals.total_value)} tone="neutral" testId="fin-kpi-total" />
        <KpiTile label="Collected"   value={inr(totals.total_paid)}  tone="success" testId="fin-kpi-paid" />
        <KpiTile label="Outstanding" value={inr(totals.total_unpaid)} tone="warning" testId="fin-kpi-unpaid" />
        <KpiTile label="Overdue"     value={inr(totals.overdue_amount)} tone="danger" testId="fin-kpi-overdue" />
        <KpiTile label="Projects · Clients" value={`${totals.project_count} · ${totals.client_count}`} tone="neutral" testId="fin-kpi-count" />
      </div>

      <div className="flex justify-between items-center">
        <div className="text-[12px] text-[hsl(var(--foreground-muted))]">As of {data.as_of}</div>
        <ExportButtons reportKey="financial_summary" filters={filters} />
      </div>

      {/* V02 — visual charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <RevenueOutstandingBar totals={totals} />
        <PerProjectPaymentBars rows={data.by_project || []} />
      </div>

      {data.trips_summary && (
        <div className="card p-3 elev-1">
          <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">Trips & site visits</div>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[13px]">
            <div><div className="text-[hsl(var(--foreground-subtle))] text-[11px]">Trip count</div><div className="font-semibold">{data.trips_summary.trip_count}</div></div>
            <div><div className="text-[hsl(var(--foreground-subtle))] text-[11px]">Billable</div><div className="font-semibold">{inr(data.trips_summary.billable_amount)}</div></div>
            <div><div className="text-[hsl(var(--foreground-subtle))] text-[11px]">Billed</div><div className="font-semibold">{inr(data.trips_summary.billed_amount)}</div></div>
            <div><div className="text-[hsl(var(--foreground-subtle))] text-[11px]">Pending bill</div><div className="font-semibold text-[hsl(var(--warning))]">{inr(data.trips_summary.pending_bill_amount)}</div></div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="card overflow-x-auto elev-1">
          <div className="p-3 border-b text-[12px] font-semibold" style={{ borderColor: 'hsl(var(--border))' }}>By project</div>
          <table className="tbl">
            <thead><tr><th>Project</th><th>Client</th><th>Total</th><th>Paid</th><th>Unpaid</th><th>Overdue</th></tr></thead>
            <tbody>
              {data.by_project.map(r => (
                <tr key={r.id}>
                  <td className="font-medium">{r.project_name}</td>
                  <td>{r.client}</td>
                  <td className="tabular-nums">{inr(r.total_amount)}</td>
                  <td className="tabular-nums text-[hsl(var(--success))]">{inr(r.paid_amount)}</td>
                  <td className="tabular-nums">{inr(r.unpaid_amount)}</td>
                  <td className={`tabular-nums ${r.overdue_amount > 0 ? 'text-[hsl(var(--danger))] font-medium' : ''}`}>{inr(r.overdue_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card overflow-x-auto elev-1">
          <div className="p-3 border-b text-[12px] font-semibold" style={{ borderColor: 'hsl(var(--border))' }}>By client</div>
          <table className="tbl">
            <thead><tr><th>Client</th><th>Projects</th><th>Total</th><th>Paid</th><th>Unpaid</th><th>Overdue</th></tr></thead>
            <tbody>
              {data.by_client.map(r => (
                <tr key={r.client}>
                  <td className="font-medium">{r.client}</td>
                  <td className="tabular-nums">{r.project_count}</td>
                  <td className="tabular-nums">{inr(r.total_amount)}</td>
                  <td className="tabular-nums text-[hsl(var(--success))]">{inr(r.paid_amount)}</td>
                  <td className="tabular-nums">{inr(r.unpaid_amount)}</td>
                  <td className={`tabular-nums ${r.overdue_amount > 0 ? 'text-[hsl(var(--danger))] font-medium' : ''}`}>{inr(r.overdue_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data.overdue_items.length > 0 && (
        <div className="card overflow-x-auto elev-1">
          <div className="p-3 border-b flex items-center gap-2" style={{ borderColor: 'hsl(var(--border))' }}>
            <AlertTriangle size={14} className="text-[hsl(var(--danger))]" />
            <span className="text-[12px] font-semibold">Overdue payments · {data.overdue_items.length} item(s)</span>
          </div>
          <table className="tbl">
            <thead><tr><th>Project</th><th>Client</th><th>Milestone</th><th>Target</th><th>Days overdue</th><th>Status</th><th>Amount</th></tr></thead>
            <tbody>
              {data.overdue_items.map((r, i) => (
                <tr key={i}>
                  <td className="font-medium">{r.project_name}</td>
                  <td>{r.client}</td>
                  <td>{r.milestone}</td>
                  <td className="tabular-nums">{r.target_date}</td>
                  <td className="tabular-nums text-[hsl(var(--danger))] font-medium">{r.days_overdue}</td>
                  <td><StatusChip status={r.status} size="sm" /></td>
                  <td className="tabular-nums font-medium">{inr(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ------------------------------ TEAM WORKLOAD ------------------------------
const TeamWorkloadTab = ({ filters }) => {
  const [data, loading] = useInsight('team-workload', filters);
  if (loading || !data) return <LoadingCard />;
  const totalItems = data.rows.reduce((s, r) => s + r.total_open_items, 0);
  const maxTotal = data.rows.reduce((m, r) => Math.max(m, r.total_open_items || 0), 0);
  const overloaded = data.rows.filter(r => (r.overdue_commitments + r.overdue_milestones) >= 3).length;
  return (
    <div className="space-y-3" data-testid="reports-team-workload">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <KpiTile label="Teammates" value={data.user_count} tone="neutral" testId="tw-kpi-people" />
        <KpiTile label="Total open items" value={totalItems} tone="neutral" testId="tw-kpi-items" />
        <KpiTile label="Average per person"
                 value={data.user_count ? (totalItems / data.user_count).toFixed(1) : '0'} tone="info"
                 testId="tw-kpi-avg" />
        <KpiTile label="Overloaded (≥3 overdue)" value={overloaded} tone={overloaded > 0 ? 'danger' : 'neutral'} testId="tw-kpi-overloaded" />
      </div>

      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="text-[12px] text-[hsl(var(--foreground-muted))]">As of {data.as_of}</div>
        <div className="flex items-center gap-3 flex-wrap">
          <WorkloadHeatLegend />
          <ExportButtons reportKey="team_workload" filters={filters} />
        </div>
      </div>

      <div className="card overflow-x-auto elev-1">
        <table className="tbl">
          <thead>
            <tr><th>Teammate</th><th>Role</th><th className="min-w-[260px]">Workload</th><th>Open</th><th>Overdue</th><th>Due ≤7d</th><th>Milestones</th><th>Projects</th></tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.user_id} data-testid={`tw-row-${r.user_id}`}>
                <td className="font-medium">{r.name}</td>
                <td><span className="chip chip-neutral">{r.role}</span></td>
                <td className="min-w-[260px]"><WorkloadHeatBar row={r} maxTotal={maxTotal} /></td>
                <td className="tabular-nums">{r.open_commitments}</td>
                <td className={`tabular-nums ${r.overdue_commitments > 0 ? 'text-[hsl(var(--danger))] font-medium' : ''}`}>{r.overdue_commitments}</td>
                <td className={`tabular-nums ${r.due_soon_commitments > 0 ? 'text-[hsl(var(--warning))]' : ''}`}>{r.due_soon_commitments}</td>
                <td className="tabular-nums">
                  <span className="text-[hsl(var(--foreground))]">{r.open_milestones}</span>
                  {r.overdue_milestones > 0 && (
                    <span className="ml-1 text-[10px] chip status-overdue">{r.overdue_milestones} od</span>
                  )}
                </td>
                <td className="text-[12px] max-w-[280px] text-[hsl(var(--foreground-muted))]">
                  {r.active_projects}
                  <span className="text-[hsl(var(--foreground-subtle))]"> · </span>
                  {(r.projects || []).slice(0, 2).map(p => p.project_name).join(', ')}
                  {(r.projects || []).length > 2 && <span> +{r.projects.length - 2} more</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------- MILESTONE TRACKER ----------------------------
const BUCKET_META = {
  all:            { label: 'All',           chipClass: '' },
  overdue:        { label: 'Overdue',       chipClass: 'status-overdue' },
  due_this_week:  { label: 'Due this week', chipClass: 'status-pending' },
  upcoming:       { label: 'Upcoming',      chipClass: 'status-in-progress' },
  done:           { label: 'Done',          chipClass: 'status-done' },
  no_date:        { label: 'No date',       chipClass: 'status-cancelled' },
};

const MilestoneTrackerTab = ({ filters }) => {
  const [data, loading] = useInsight('milestone-tracker', filters);
  const [bucket, setBucket] = useState('all');
  if (loading || !data) return <LoadingCard />;

  const filteredRows = bucket === 'all' ? data.rows : data.rows.filter(r => r.bucket === bucket);

  return (
    <div className="space-y-3" data-testid="reports-milestone-tracker">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KpiTile label="Overdue"      value={data.counts.overdue || 0}       tone="danger"  testId="ms-kpi-overdue" />
        <KpiTile label="Due this week" value={data.counts.due_this_week || 0} tone="warning" testId="ms-kpi-week" />
        <KpiTile label="Upcoming"     value={data.counts.upcoming || 0}      tone="info"    testId="ms-kpi-upcoming" />
        <KpiTile label="Done"         value={data.counts.done || 0}          tone="success" testId="ms-kpi-done" />
        <KpiTile label="No date"      value={data.counts.no_date || 0}       tone="neutral" testId="ms-kpi-nodate" />
      </div>

      {/* V02 — slip trend + filter chips */}
      <MilestoneSlipTrend weeks={8} />

      <div className="flex items-center gap-1.5 flex-wrap" data-testid="ms-filter-chips">
        {Object.entries(BUCKET_META).map(([key, meta]) => {
          const count = key === 'all' ? data.total : (data.counts[key] || 0);
          const active = bucket === key;
          return (
            <button
              key={key}
              onClick={() => setBucket(key)}
              data-testid={`ms-chip-${key}`}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[12px] transition-all
                          ${active
                            ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/.10)] text-[hsl(var(--foreground))] font-semibold'
                            : 'border-[hsl(var(--border))] text-[hsl(var(--foreground-muted))] hover:bg-[hsl(var(--surface-2))]'}`}
            >
              {meta.label}
              <span className="tabular-nums opacity-70">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="flex justify-between items-center">
        <div className="text-[12px] text-[hsl(var(--foreground-muted))]">
          Showing {filteredRows.length} of {data.total} milestones · As of {data.as_of}
        </div>
        <ExportButtons reportKey="milestone_tracker" filters={filters} />
      </div>

      <div className="card overflow-x-auto elev-1">
        <table className="tbl">
          <thead>
            <tr><th>Target date</th><th>Bucket</th><th>Milestone</th><th>Owner</th><th>Project</th><th>Client</th><th>Status</th><th>Amount</th><th>Leads</th></tr>
          </thead>
          <tbody>
            {filteredRows.map((r, i) => (
              <tr key={i} data-testid={`ms-row-${i}`} className={r.is_overdue ? 'bg-[hsl(var(--danger-bg)/.4)]' : ''}>
                <td className="tabular-nums font-mono text-[12px]">{r.target_date || '—'}</td>
                <td>
                  {r.bucket === 'overdue' && <span className="chip status-overdue"><AlertTriangle size={10} />Overdue</span>}
                  {r.bucket === 'due_this_week' && <span className="chip status-pending">Due this week</span>}
                  {r.bucket === 'upcoming' && <span className="chip status-in-progress">Upcoming</span>}
                  {r.bucket === 'done' && <span className="chip status-done"><CheckCircle2 size={10} />Done</span>}
                  {r.bucket === 'no_date' && <span className="chip status-cancelled">No date</span>}
                </td>
                <td className="font-medium">{r.milestone_name}</td>
                <td className="text-[12.5px]">{r.owner_name || <span className="italic text-[hsl(var(--foreground-subtle))]">Unassigned</span>}</td>
                <td>{r.project_name}</td>
                <td>{r.client}</td>
                <td><StatusChip status={r.status} size="sm" /></td>
                <td className="tabular-nums"><MaskedAmount amount={r.amount} masked={false} /></td>
                <td className="text-[12px] text-[hsl(var(--foreground-muted))]">
                  {[r.creative_lead_name, r.technical_lead_name].filter(Boolean).join(' · ') || '—'}
                </td>
              </tr>
            ))}
            {filteredRows.length === 0 && (
              <tr><td colSpan={9} className="py-8 text-center text-[hsl(var(--foreground-muted))]" data-testid="ms-empty">
                No milestones in this bucket right now.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------- EXPORT BUILDER (legacy CSV) ------------------
const ExportBuilderTab = () => {
  const [datasets, setDatasets] = useState([]);
  const [presets, setPresets] = useState([]);
  const [ds, setDs] = useState('');
  const [cols, setCols] = useState([]);
  const [filters, setFilters] = useState({});
  const [preview, setPreview] = useState(null);
  const [presetName, setPresetName] = useState('');

  const load = async () => {
    try {
      const [d, p] = await Promise.all([api.get('/reports/datasets'), api.get('/reports/presets')]);
      setDatasets(d.data.datasets || []); setPresets(p.data || []);
    } catch (e) { toast.error(parseErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const current = datasets.find(x => x.key === ds);

  const pick = (k) => {
    setDs(k);
    const dsm = datasets.find(x => x.key === k);
    setCols((dsm?.default_columns || []).slice());
    setPreview(null);
  };

  const runPreview = async () => {
    try {
      const r = await api.post('/reports/run', { dataset: ds, columns: cols, filters, format: 'json' });
      setPreview(r.data);
    } catch (e) { toast.error(parseErr(e)); }
  };

  const download = async (fmt) => {
    try {
      const token = localStorage.getItem('32d.os.token');
      const res = await fetch(`${API_BASE}/reports/run`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dataset: ds, columns: cols, filters, format: fmt }),
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      const ext = fmt === 'xlsx' ? 'xlsx' : fmt === 'pdf' ? 'pdf' : 'csv';
      a.download = `32DEC-${ds}.${ext}`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ext.toUpperCase()}`);
    } catch (e) { toast.error(String(e).slice(0, 200)); }
  };

  const savePreset = async () => {
    if (!presetName || !ds) return;
    try {
      await api.post('/reports/presets', { name: presetName, dataset: ds, columns: cols, filters, schedule_weekly: false });
      toast.success('Preset saved'); setPresetName(''); load();
    } catch (e) { toast.error(parseErr(e)); }
  };
  const loadPreset = (p) => { setDs(p.dataset); setCols(p.columns || []); setFilters(p.filters || {}); setPreview(null); };
  const deletePreset = async (id) => {
    if (!window.confirm('Delete this preset?')) return;
    try { await api.delete(`/reports/presets/${id}`); load(); } catch (e) { toast.error(parseErr(e)); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-4" data-testid="reports-export-builder">
      <div className="space-y-3">
        <div className="card p-3 elev-1">
          <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">Datasets</div>
          <div className="mt-2 space-y-1">
            {datasets.map(d => (
              <button key={d.key} onClick={() => pick(d.key)} data-testid={`report-ds-${d.key}`}
                      className={`w-full text-left px-3 py-1.5 rounded-md text-[13px] transition hover:bg-[hsl(var(--surface-2))] ${ds === d.key ? 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] font-medium' : ''}`}>
                {d.label}
              </button>
            ))}
            {datasets.length === 0 && <div className="text-[12px] text-[hsl(var(--foreground-muted))] px-2 py-3">No datasets available for your role.</div>}
          </div>
        </div>
        <div className="card p-3 elev-1">
          <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">Saved presets</div>
          <div className="mt-2 space-y-1">
            {presets.map(p => (
              <div key={p.id} className="flex items-center gap-1" data-testid={`preset-${p.id}`}>
                <button className="flex-1 text-left px-2 py-1 rounded-md text-[13px] hover:bg-[hsl(var(--surface-2))]" onClick={() => loadPreset(p)}>
                  {p.name} <span className="text-[10.5px] text-[hsl(var(--foreground-subtle))]">· {p.dataset}</span>
                </button>
                <button onClick={() => deletePreset(p.id)} className="p-1 text-[hsl(var(--danger))]"><Trash2 size={12} /></button>
              </div>
            ))}
            {presets.length === 0 && <div className="text-[12px] text-[hsl(var(--foreground-muted))] px-2 py-2">No presets saved yet.</div>}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {!ds ? (
          <div className="card p-8 text-center text-[13px] text-[hsl(var(--foreground-muted))] elev-1">Select a dataset on the left to begin.</div>
        ) : (
          <>
            <div className="card p-3 elev-1">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="font-heading font-semibold">{current?.label} — columns</div>
                <div className="flex gap-1 flex-wrap">
                  <Button size="sm" variant="outline" onClick={runPreview} data-testid="report-preview"><Play size={13} className="mr-1" />Preview</Button>
                  <Button size="sm" variant="outline" onClick={() => download('xlsx')} data-testid="report-download-xlsx"><FileSpreadsheet size={13} className="mr-1" />Excel</Button>
                  <Button size="sm" variant="outline" onClick={() => download('pdf')} data-testid="report-download-pdf"><FileText size={13} className="mr-1" />PDF</Button>
                  <Button size="sm" onClick={() => download('csv')} data-testid="report-download"><FileDown size={13} className="mr-1" />CSV</Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(current?.default_columns || []).map(c => (
                  <label key={c} className="inline-flex items-center gap-1 border rounded-md px-2 py-1 text-[12px] cursor-pointer transition hover:bg-[hsl(var(--surface-2))]" style={{ borderColor: 'hsl(var(--border))' }}>
                    <input type="checkbox" checked={cols.includes(c)} onChange={e => setCols(e.target.checked ? [...cols, c] : cols.filter(x => x !== c))} data-testid={`col-${c}`} />
                    {c}
                  </label>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Input placeholder="Preset name (e.g. Pending invoices)" value={presetName} onChange={e => setPresetName(e.target.value)} className="max-w-[280px]" data-testid="preset-name" />
                <Button size="sm" variant="outline" onClick={savePreset} disabled={!presetName || !ds} data-testid="preset-save"><Save size={13} className="mr-1" />Save preset</Button>
              </div>
            </div>
            {preview && (
              <div className="card overflow-x-auto elev-1" data-testid="report-preview-table">
                <div className="p-3 text-[12px] text-[hsl(var(--foreground-muted))]">Preview — {preview.row_count} rows</div>
                <table className="tbl">
                  <thead><tr>{preview.columns.map(c => <th key={c}>{c}</th>)}</tr></thead>
                  <tbody>
                    {(preview.rows || []).slice(0, 30).map((r, i) => (
                      <tr key={i}>{preview.columns.map(c => <td key={c} className="text-[12px]">{String(r[c] ?? '')}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const LoadingCard = () => (
  <div className="card p-8 elev-1 text-center text-[13px] text-[hsl(var(--foreground-muted))]">
    <RefreshCcw size={16} className="inline mr-2 animate-spin" /> Loading…
  </div>
);
